import { forwardRef, useEffect, useState } from "react";
import { WidgetProps, WidgetRef } from "../../../types/widget";
import { styles } from "../styles";
import { DateRangeInput } from "../../inputs";
import { DateRange } from "../../../types";
import { Row } from "../../containers";
import {
  getRangeLastMonth,
  getRangeLast3Month,
  getRangeLast6Month,
  getRangeLastYear,
} from "../../../utils";
import Options from "../../options";
import { Option } from "../../options/types";
import { useThemeStore } from "../../../stores";
import Api from "../../../api/api";
import { ISCDataset, ISCRangeMap, ISCResponse } from "./types";
import { formatID, parseISCResponseData } from "./utils";
import { LineChart, axisClasses } from "@mui/x-charts";
import ChartLegend from "../../chart-legend";

const DATE_RANGE_OPTIONS: Option<DateRange>[] = [
  {
    title: "یک ماه اخیر",
    value: { from: getRangeLastMonth().from, to: getRangeLastMonth().to },
  },
  {
    title: "سه ماه اخیر",
    value: { from: getRangeLast3Month().from, to: getRangeLast3Month().to },
  },
  {
    title: "شش ماه اخیر",
    value: { from: getRangeLast6Month().from, to: getRangeLast6Month().to },
  },
  {
    title: "یک سال اخیر",
    value: { from: getRangeLastYear().from, to: getRangeLastYear().to },
  },
];

let i = 0;

const IncomeSpendChartWidget = forwardRef<WidgetRef, WidgetProps>(
  (props, ref) => {
    const theme = useThemeStore((s) => s.theme);

    const [dateRange, setDateRange] = useState<DateRange>({
      from: getRangeLastYear().from,
      to: getRangeLastYear().to,
    });

    const [selectedOption, setSelectedOption] = useState<Option<DateRange>>({
      title: "",
      value: null!,
    });

    useEffect(() => {
      if (!i) {
        i++;
        return;
      }

      setSelectedOption({ title: "", value: null! });
    }, [dateRange]);

    const [range, setRange] = useState<keyof ISCRangeMap>("monthly");
    const [data, setData] = useState<ISCDataset>([]);

    useEffect(() => {
      let range = selectedOption.value ?? dateRange;

      Api.getData<ISCResponse>(
        `/widget/income_spend_chart?from=${range.from}&to=${range.to}&range=daily`
      )
        .then((res) => {
          setRange(res.range);
          setData(
            parseISCResponseData(
              selectedOption.value ?? dateRange,
              res.range,
              res.data
            )
          );
        })
        .catch((err) => {
          console.error(err);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption]);

    return (
      <div style={styles.container} ref={ref}>
        <Row gap={10} className="isc-header">
          <DateRangeInput
            range={dateRange}
            setRange={setDateRange}
            style={{
              borderColor: selectedOption.value ? theme.icon : theme.primary,
            }}
            className="transition"
          />

          <Options<DateRange>
            options={DATE_RANGE_OPTIONS}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        </Row>

        <div
          style={{
            background: theme.overlay,
            borderRadius: 15,
            minHeight: 100,
            marginTop: 8,
          }}
        >
          <LineChart
            dataset={data}
            series={[
              { dataKey: "spent_sum", color: theme.alert, label: "مخارج" },
              {
                dataKey: "received_sum",
                color: theme.secondary,
                label: "درآمد",
              },
            ]}
            xAxis={[
              {
                dataKey: "_id",
                valueFormatter: (value) => formatID(range, value),
                scaleType: "point",
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: {
                  textAnchor: "start",
                  angle: 45
                }
              },
            ]}
            sx={() => ({
              [`.${axisClasses.root}`]: {
                [`.${axisClasses.tick}, .${axisClasses.tickLabel}, .${axisClasses.line}`]:
                  {
                    stroke: theme.textOnOverlay,
                  },
              },
              ".MuiMarkElement-root": {
                fill: theme.overlay,
              }
            })}
            slotProps={{ legend: { hidden: true } }}
            height={300}
          />

          <Row gap={50} style={{margin: "1rem 3rem", padding: "0 0 2rem 0"}} >
            <ChartLegend color={theme.alert} label="مخارج" />
            <ChartLegend color={theme.secondary} label="درآمد" />
          </Row>
        </div>
      </div>
    );
  }
);

export default IncomeSpendChartWidget;
